import dayjs from 'dayjs'
import { ReactElement } from 'react'
import {
  Alert,
  Box,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { EventRepeat, Clear } from '@mui/icons-material'
import type { Recurrence, RecurrenceType } from 'src/v3/core/types'
import { paperInputSx } from '../../styles'

type RecurrenceFieldsProps = {
  recurrence: Recurrence | undefined
  goLiveDate: string
  onRecurrenceChange: (recurrence: Recurrence) => void
  errors?: {
    recurrenceInterval?: string
    recurrenceEndDate?: string
  }
}

export default function RecurrenceFields({
  recurrence,
  goLiveDate,
  onRecurrenceChange,
  errors = {},
}: RecurrenceFieldsProps): ReactElement {
  let dayOfMonth = goLiveDate ? dayjs(goLiveDate).date() : 1

  let handleFieldChange = (field: keyof Recurrence, value: any) => {
    onRecurrenceChange({
      ...recurrence,
      recurrenceType: recurrence?.recurrenceType || 'Monthly',
      recurrenceInterval: recurrence?.recurrenceInterval || 1,
      recurrenceEndDate: recurrence?.recurrenceEndDate || '',
      dayOfMonth,
      [field]: value,
    })
  }

  let recurrenceType = recurrence?.recurrenceType || 'Monthly'
  let recurrenceInterval = recurrence?.recurrenceInterval || 1
  let recurrenceEndDate = recurrence?.recurrenceEndDate || ''

  let getDaySuffix = (day: number): string => {
    if (day >= 11 && day <= 13) return 'th'
    switch (day % 10) {
      case 1:
        return 'st'
      case 2:
        return 'nd'
      case 3:
        return 'rd'
      default:
        return 'th'
    }
  }

  let getRecurrenceSummary = (): string => {
    if (!goLiveDate) {
      return 'Please select a go-live date to see the recurrence schedule'
    }

    let firstDate = dayjs(goLiveDate)

    // For monthly, use the day from go-live date and add interval months
    // For yearly, just add the interval years
    let nextDate =
      recurrenceType === 'Monthly'
        ? firstDate.add(recurrenceInterval, 'month')
        : firstDate.add(recurrenceInterval, 'year')

    let intervalText =
      recurrenceType === 'Monthly'
        ? recurrenceInterval === 1
          ? `monthly on the ${dayOfMonth}${getDaySuffix(dayOfMonth)}`
          : `every ${recurrenceInterval} months on the ${dayOfMonth}${getDaySuffix(dayOfMonth)}`
        : recurrenceInterval === 1
          ? 'annually'
          : `every ${recurrenceInterval} years`

    let endText = recurrenceEndDate
      ? `until ${dayjs(recurrenceEndDate).format('MMMM D, YYYY')}`
      : 'indefinitely or until you cancel the schedule'

    return `The assignment will be created on ${firstDate.format('MMMM D, YYYY')} and will be created again on ${nextDate.format('MMMM D, YYYY')}, repeating ${intervalText} ${endText}.`
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
          flexWrap: 'wrap',
          mb: 3,
        }}
      >
        <Typography variant="body1" sx={{ pt: 2 }}>
          Repeat every
        </Typography>

        <TextField
          type="number"
          value={recurrenceInterval}
          onChange={(e) =>
            handleFieldChange(
              'recurrenceInterval',
              parseInt(e.target.value, 10) || 1,
            )
          }
          error={!!errors.recurrenceInterval}
          helperText={errors.recurrenceInterval}
          sx={{ width: 100, ...paperInputSx }}
          slotProps={{
            htmlInput: { min: 1, max: 12 },
          }}
        />

        <FormControl sx={{ minWidth: 120, ...paperInputSx }}>
          <Select
            value={recurrenceType}
            onChange={(e) =>
              handleFieldChange(
                'recurrenceType',
                e.target.value as RecurrenceType,
              )
            }
          >
            <MenuItem value="Monthly">months</MenuItem>
            <MenuItem value="Yearly">years</MenuItem>
          </Select>
        </FormControl>

        <Typography variant="body1" sx={{ pt: 2 }}>
          until
        </Typography>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="End Date (Optional)"
            value={recurrenceEndDate ? dayjs(recurrenceEndDate) : null}
            onChange={(date) =>
              handleFieldChange('recurrenceEndDate', date?.toISOString() || '')
            }
            minDate={
              goLiveDate
                ? dayjs(goLiveDate).add(1, 'day')
                : dayjs().add(2, 'day')
            }
            slotProps={{
              textField: {
                error: !!errors.recurrenceEndDate,

                helperText:
                  errors.recurrenceEndDate ||
                  'Leave blank to repeat indefinitely',

                sx: { minWidth: 200 },

                slotProps: {
                  input: {
                    sx: { bgcolor: 'background.paper' },
                    endAdornment: recurrenceEndDate ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleFieldChange('recurrenceEndDate', '')
                          }
                          edge="end"
                        >
                          <Clear fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  },
                },
              },
            }}
          />
        </LocalizationProvider>
      </Box>
      <Alert severity="info" icon={<EventRepeat />}>
        <Typography variant="body2">{getRecurrenceSummary()}</Typography>
      </Alert>
    </Box>
  )
}
